import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ValidatorComponent } from '@feature/survey-management/components/validator/validator.component';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { IconComponent } from '@shared/components/icon/icon.component';
import { Icon } from '@shared/components/icon/icon.enum';
import { ValidatorType } from '@shared/models/enums/validator-type.enum';
import {
  AlternativeAnswerSingle,
  SingleSelect,
} from '@shared/models/question-types/single-select';
import { SafeHtmlPipe } from '../../../../../../../shared/pipes/safe-html.pipe';
import { StripHtmlPipe } from '../../../../../../../shared/pipes/strip-html.pipe';

@Component({
  selector: 'app-single-select-radio-button',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    IconComponent,
    ValidatorComponent,
    SafeHtmlPipe,
    StripHtmlPipe,
  ],
  templateUrl: './single-select-radio-button.component.html',
  styleUrl: './single-select-radio-button.component.scss',
})
export class SingleSelectRadioButtonComponent implements OnInit {
  @Input({ required: true }) question!: SingleSelect;
  @Input({ required: true }) alternative!: AlternativeAnswerSingle;
  @Input({ required: true }) control!: any;
  @Input() otherControl?: any;
  @Input() inTable: boolean = false;
  @Input() columnTitle?: string;
  @Input() cellHeaders?: string;
  @Input() secondColumnStart: boolean = false;
  @Input() quotaErrorAlternativeId?: string;
  @ViewChild('otherInput') otherInput?: ElementRef<HTMLTextAreaElement>;
  @Input() parentTableSelector: string = 'app-multi-matrix-table';

  Icon = Icon;
  ValidatorType = ValidatorType;
  alternativeValue: string = '';

  get hasQuotaError(): boolean {
    return this.quotaErrorAlternativeId === this.alternative.id;
  }

  get isOtherSelected(): boolean {
    return (
      ((this.control?.value === this.alternative.id &&
        !!this.otherControl &&
        this.alternative.type === 'OTHER') ||
        (this.question.inPrintView && this.alternative.type === 'OTHER')) ??
      false
    );
  }

  constructor(
    private store: Store,
    private elementRef: ElementRef,
  ) {}

  ngOnInit(): void {
    this.alternativeValue = this.alternative.id;

    if (this.question.inPrintView && this.otherControl) {
      this.otherControl.enable();
    }
  }

  toggleSelection(value: string, type: string) {
    const previousValue = this.control.value;
    const isDeselecting = this.control.value === value;
    const wasOtherSelected =
      previousValue && this.getAlternativeType(previousValue) === 'OTHER';

    if (isDeselecting) {
      this.control.setValue('');
    } else {
      this.control.setValue(value);
    }

    // Handle OTHER control state
    if (this.otherControl) {
      if (!isDeselecting && type === 'OTHER') {
        this.otherControl.enable();
        setTimeout(() => {
          if (this.otherInput?.nativeElement) {
            this.otherInput.nativeElement.focus();

            setTimeout(() => {
              const cell = this.otherInput?.nativeElement?.closest('td');
              cell?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'end',
              });
            }, 200);

            if (this.inTable) {
              this.recalculateTableOverflow();
            }
          }
        }, 100);
      } else if (isDeselecting || (!isDeselecting && type !== 'OTHER')) {
        this.otherControl?.disable();

        if (this.inTable) {
          setTimeout(() => {
            this.recalculateTableOverflow();
          }, 100);
        }
      }
    }
  }

  /**
   * Helper to get the type of an alternative by its value
   */
  private getAlternativeType(value: string): string {
    const alternative = this.question.alternatives?.find(
      (alt) => alt.id === value,
    );
    return alternative?.type || '';
  }
  private recalculateTableOverflow(): void {
    if (this.inTable && this.parentTableSelector) {
      const parentComponent = this.elementRef.nativeElement.closest(
        this.parentTableSelector,
      );

      if (parentComponent) {
        parentComponent.dispatchEvent(new CustomEvent('recalculate-overflow'));
      }
    }
  }

  handleKeydown(event: KeyboardEvent, value: string, type: string) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleSelection(value, type);
    }
  }
}
